import {
  buildTelUri,
  formatPhoneNumber,
  getCountryFlagEmoji,
} from '@/lib/crm/utils/phone';
import type { PhoneNumber } from '@/types/crm';

import { CopyOnHoverValue } from './CopyOnHoverValue';

export interface PhoneValueLabelProps {
  value: PhoneNumber | null | undefined;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** Enable CSS truncation with ellipsis and title tooltip */
  truncate?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

/**
 * Display component for phone numbers.
 * Shows formatted phone number as a clickable tel: link with copy button on hover.
 *
 * Used in:
 * - Contact lists
 * - Contact detail views
 * - Any read-only phone display
 *
 * The country's flag precedes the number, matching the country picker in
 * PhoneInput so a record reads the same whether you're viewing or editing it.
 * It's decorative: the dial code already carries the country, the flag is
 * aria-hidden, and copy/tel/title all stay the number alone so copying a cell
 * still yields something dialable.
 *
 * @example
 * <PhoneValueLabel value={{country_code: "US", national_number: "4155552671"}} />
 * // Renders: <a href="tel:+14155552671">🇺🇸 +1 415 555 2671</a> with copy button
 */
export const PhoneValueLabel: React.FC<PhoneValueLabelProps> = ({
  value,
  fontSize = 'sm',
  className = '',
  truncate,
}) => {
  const sizeClass = fontSizeClasses[fontSize];
  const truncateClasses = truncate
    ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
    : '';

  if (!value) {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  const formatted = formatPhoneNumber(value);
  const telUri = buildTelUri(value);
  const flag = getCountryFlagEmoji(value.country_code);

  return (
    <CopyOnHoverValue
      copyText={formatted}
      copyLabel='Copy phone number'
      className={className}
      truncate={truncate}
    >
      <a
        href={encodeURI(telUri)}
        onClick={e => e.stopPropagation()}
        className={`${sizeClass} ${truncateClasses}`}
        title={truncate ? formatted : undefined}
        aria-label={`Call ${formatted}`}
      >
        {/* Inside the anchor, not beside it: the anchor is the block box that
            owns ellipsis when `truncate` is set, so a sibling flag would either
            break onto its own line or need a flex wrapper that costs the pill
            its inline baseline (see CopyOnHoverValue's layout notes). */}
        {flag && (
          <span aria-hidden='true' className='mr-1.5'>
            {flag}
          </span>
        )}
        <span>{formatted}</span>
      </a>
    </CopyOnHoverValue>
  );
};
